import { create } from 'zustand';
import {
  BasicCouponFragment,
  CompleteStoreFragment,
  GetStoreDocument,
  Maybe,
} from 'drycut-graphql';
import { initApolloClient } from 'drycut-apollo';
import { DateTime } from 'luxon';
import { devtools } from 'zustand/middleware';

type CheckoutState = {
  manual: boolean;
  selectedStore?: Maybe<CompleteStoreFragment>;
  scheduled: boolean;
  selectedSchedule?: DateTime;
  selectedCoupon?: BasicCouponFragment;
};

type CheckoutActions = {
  setSelectedStore: (slug: string) => void;
  toggleScheduled: () => void;
  setSelectedSchedule: (date?: DateTime) => void;
  setSelectedCoupon: (coupon?: BasicCouponFragment) => void;
};

export const useCheckoutStore = create<CheckoutState & CheckoutActions>()(
  devtools((set): CheckoutState & CheckoutActions => {
    return {
      // State
      manual: false,
      selectedStore: undefined,
      scheduled: false,
      selectedSchedule: undefined,
      selectedCoupon: undefined,

      setSelectedStore: async (slug) => {
        try {
          const client = initApolloClient();

          const { data } = await client.query({
            query: GetStoreDocument,
            variables: { slug },
          });
          const store: Maybe<CompleteStoreFragment> = data?.store || null;

          set(
            { selectedStore: store, selectedSchedule: undefined, scheduled: false },
            undefined,
            'setSelectedStore',
          );
        } catch {
          // Swallow — captureUnhandledRejections would have caught this anyway,
          // but we catch here to prevent crashing React's commit phase.
          // selectedStore stays undefined, UI degrades gracefully.
          // This avoids https://app.rollbar.com/a/drycut/fix/item/ReactFrontend/376#occurrences
        }
      },

      toggleScheduled: () => {
        // Using the value from useBoolean hook
        set((state) => ({ scheduled: !state.scheduled }), undefined, 'toggleScheduled');
      },

      setSelectedSchedule: (date?: DateTime) => {
        set({ selectedSchedule: date, scheduled: Boolean(date) }, undefined, 'setSelectedSchedule');
      },

      setSelectedCoupon: (coupon?: BasicCouponFragment) => {
        set({ selectedCoupon: coupon }, undefined, 'setSelectedCoupon');
      },
    };
  }),
);
